import { ComponentType, ConnectionPositionPair, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { ElementRef, Injectable, InjectionToken, Injector, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';

import { PopoverConfig } from './popover-config';
import { PopoverRef } from './popover-ref';
import { PopoverComponent } from './popover/popover.component';

/**
 * Injection token that can be used to access the data that was passed in to a popover.
 * */
export const POPOVER_DATA = new InjectionToken('popover.data');

const defaultConfig: PopoverConfig<unknown> = {
  backdropClass: '',
  disableClose: false,
  panelClass: 'popover-panel',
  arrowOffset: 35,
  arrowSize: 30,
  width: 320,
  minHeight: 159
};

/**
 * Service to open modal and manage popovers.
 */
@Injectable({
  providedIn: 'root'
})
export class PopoverService {
  constructor(private readonly overlay: Overlay, private readonly injector: Injector, private readonly router: Router) { }

  open<D = unknown>(componentOrTemplate: ComponentType<unknown> | TemplateRef<unknown>,
      target: ElementRef | HTMLElement,
      config: Partial<PopoverConfig<D>> = {}): PopoverRef<D> {
    const popoverConfig: PopoverConfig = Object.assign({}, defaultConfig, config);

    const arrowSize = popoverConfig.arrowSize;
    const arrowOffset = popoverConfig.arrowOffset;
    const panelOffset = arrowSize / 2;

    // preferred positions, in order of priority
    const positions: ConnectionPositionPair[] = [
      // top center
      {
        overlayX: 'center',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'center'],
        offsetY: -1 * panelOffset
      },
      // top left
      {
        overlayX: 'start',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'left'],
        offsetX: -1 * arrowOffset,
        offsetY: -1 * panelOffset
      },
      // top right
      {
        overlayX: 'end',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'right'],
        offsetX: arrowOffset,
        offsetY: -1 * panelOffset
      },
      // bottom center
      {
        overlayX: 'center',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'center'],
        offsetY: panelOffset
      },
      // bottom left
      {
        overlayX: 'start',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'left'],
        offsetX: -1 * arrowOffset,
        offsetY: panelOffset
      },
      // bottom right
      {
        overlayX: 'end',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'right'],
        offsetX: arrowOffset,
        offsetY: panelOffset
      }
    ];

    const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(target)
        .withPush(false)
        .withFlexibleDimensions(false)
        .withPositions(positions);
    const scrollStrategy = this.overlay.scrollStrategies.reposition();

    const overlayRef = this.overlay.create({...popoverConfig, positionStrategy, scrollStrategy});
    const popoverRef = new PopoverRef(overlayRef, positionStrategy, popoverConfig, this.router);

    const popover = overlayRef.attach(new ComponentPortal(
      PopoverComponent,
      null,
      Injector.create({
        providers: [
          {provide:POPOVER_DATA, useValue: config.data},
          {provide:PopoverRef, useValue:popoverRef}
        ],
        parent: this.injector
      })
    )).instance;

    if (componentOrTemplate instanceof TemplateRef) {
      // rendering a provided template dynamically
      popover.attachTemplatePortal(
        new TemplatePortal(
          componentOrTemplate,
          null,
          {
            $implicit: config.data,
            popover: popoverRef
          }
        )
      );
    } else {
      // rendering a provided component dynamically
      popover.attachComponentPortal(
        new ComponentPortal(
          componentOrTemplate,
          null,
          Injector.create({
            providers: [
              {provide:POPOVER_DATA, useValue: config.data},
              {provide:PopoverRef, useValue:popoverRef}
            ],
            parent: this.injector
          })
        )
      );

    }

    return popoverRef;
  }
}
